import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { filter, Subscription } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { NavCountsService } from '../../core/services/nav-counts.service';
import { CaseChatDrawerComponent } from '../judge/chat/case-chat-drawer.component';

export interface NavItem {
  label: string;
  icon: string;
  route?: string;
  badge?: number;
  sectionLabel?: string; // renders sb-label section heading before this item
  isSignOut?: boolean;
  /** Extra route prefixes that should keep this item highlighted */
  activeFor?: string[];
}

export interface PortalUser {
  name: string;
  role: string;
  initials: string;
  id?: string;
}

@Component({
  selector: 'app-portal-shell',
  standalone: true,
  imports: [CommonModule, RouterModule, CaseChatDrawerComponent],
  templateUrl: './portal-shell.component.html',
})
export class PortalShellComponent implements OnInit, OnDestroy {
  portalTitle = '';
  navItems: NavItem[] = [];
  user: PortalUser = { name: '', role: '', initials: '' };
  breadcrumb = '';
  navCounts: Record<string, number> = {};

  private routerSub?: Subscription;
  private dataSub?: Subscription;
  private countsSub?: Subscription;
  private loadedCountsFor = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private navCountsService: NavCountsService,
  ) {}

  ngOnInit() {
    // Subscribe to the data observable (not snapshot) so values resolve
    // reliably during in-app navigation through the role guard.
    this.dataSub = this.route.data.subscribe(data => {
      this.portalTitle = data['portalTitle'] ?? '';
      this.navItems = data['navItems'] ?? [];

      // Prefer the real authenticated user; fall back to route mock data
      // only if there is no active session (e.g. dev/demo without login).
      this.authService.ensureSession();
      const authUser = this.authService.currentUser();
      this.user = authUser
        ? this.mapUser(authUser)
        : (data['user'] ?? { name: '', role: '', initials: '' });

      // Load live sidebar counts for this portal once per role
      const role = authUser?.role ?? data['user']?.role ?? '';
      if (role && role !== this.loadedCountsFor) {
        this.loadedCountsFor = role;
        this.navCountsService.loadFor(role);
      }
    });

    this.countsSub = this.navCountsService.counts$.subscribe(c => this.navCounts = c);

    // Hydrate the full profile (fullName / phone / address) from /me — the
    // login token only carries the basics, so this fills in the real name.
    this.authService.getMe().subscribe({
      next: u => { this.user = this.mapUser(u); },
      error: () => {},
    });

    this.updateBreadcrumb();
    this.routerSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.updateBreadcrumb());
  }

  private mapUser(u: { name: string; role: string; initials: string; email: string }) {
    return {
      name: u.name,
      role: this.capitalise(u.role),
      initials: u.initials,
      id: u.email, // footer shows email (friendlier than the raw user GUID)
    };
  }

  ngOnDestroy() {
    this.routerSub?.unsubscribe();
    this.dataSub?.unsubscribe();
    this.countsSub?.unsubscribe();
  }

  /** Live count for a nav item's route, falling back to its static badge */
  badgeFor(item: NavItem): number {
    if (item.route && this.navCounts[item.route] != null) return this.navCounts[item.route];
    return item.badge ?? 0;
  }

  /** Whether the given nav item should be considered active for the current URL */
  isActive(item: NavItem): boolean {
    const url = this.router.url.split('?')[0]; // strip query params
    if (item.route) {
      // Match segment: URL must contain /route at a boundary (e.g. /portal/judge/cases or /portal/judge/cases/123)
      const seg = '/' + item.route;
      if (url.includes(seg + '/') || url.endsWith(seg)) return true;
    }
    if (item.activeFor) return item.activeFor.some(p => url.startsWith(p));
    return false;
  }

  private updateBreadcrumb() {
    let child = this.route.firstChild;
    while (child?.firstChild) child = child.firstChild;
    this.breadcrumb = child?.snapshot.data['breadcrumb'] ?? '';
  }

  safeIcon(svg: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }

  signOut() {
    this.authService.logout();
    this.router.navigate(['/auth/login']);
  }

  get hasNotifications(): boolean {
    return (this.navItems.find(n => n.label === 'Notifications')?.badge ?? 0) > 0;
  }

  get firstName(): string {
    return this.user.name.split(' ')[0];
  }

  private capitalise(s: string): string {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  }
}
